import { Controller } from '@hotwired/stimulus';

/**
 * Cascading Select Controller
 *
 * Dynamically loads options for a select element based on the value of another select element.
 * When the source select changes, it fetches related options from a URL and populates the target select.
 * This creates a cascading dropdown pattern where selecting one option reveals related options.
 *
 * Usage:
 * ```html
 * <div data-controller="cascading-select"
 *      data-cascading-select-url-value="/api/options"
 *      data-cascading-select-source-selector-value="#plan_select"
 *      data-cascading-select-target-selector-value="#flex_account_select">
 *   <select id="plan_select" data-action="change->cascading-select#loadOptions">
 *     <option value="">Select a plan...</option>
 *   </select>
 *   <select id="flex_account_select">
 *     <option value="">Select a flex account...</option>
 *   </select>
 * </div>
 * ```
 *
 * Values:
 * - url: The endpoint URL to fetch options from (should accept a query parameter for the source value)
 * - sourceSelector: CSS selector for the source select element
 * - targetSelector: CSS selector for the target select element
 * - paramKey: Query parameter key to send the source value (default: "plan_id")
 * - responseKey: Key in the JSON response containing the options array (default: "options")
 * - labelField: Field name in each option object for the label (default: "label")
 * - valueField: Field name in each option object for the value (default: "value")
 * - placeholder: Placeholder text for empty state (default: "Select...")
 */
export default class extends Controller {
  static values = {
    url: String,
    sourceSelector: String,
    targetSelector: String,
    paramKey: { type: String, default: 'plan_id' },
    responseKey: { type: String, default: 'options' },
    labelField: { type: String, default: 'label' },
    valueField: { type: String, default: 'value' },
    placeholder: { type: String, default: 'Select...' }
  };

  connect() {
    this.sourceSelect = document.querySelector(this.sourceSelectorValue);
    this.targetSelect = document.querySelector(this.targetSelectorValue);

    if (!this.sourceSelect || !this.targetSelect) {
      return;
    }

    // Load options if source already has a value (e.g., on page load for edit forms)
    if (this.sourceSelect.value) {
      this.loadOptions();
    }
  }

  loadOptions() {
    const sourceValue = this.sourceSelect.value;

    if (!sourceValue) {
      this.clearOptions();
      return;
    }

    this.setLoading(true);

    const url = new URL(this.urlValue, window.location.origin);
    url.searchParams.set(this.paramKeyValue, sourceValue);

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        const options = data[this.responseKeyValue] || [];
        this.populateOptions(options);
      })
      .catch(() => {
        this.clearOptions();
      })
      .finally(() => {
        this.setLoading(false);
      });
  }

  populateOptions(options) {
    // Store the current selected value to preserve it after repopulating
    const currentValue = this.targetSelect.value;

    // Clear existing options except the first placeholder
    this.targetSelect.innerHTML = '';
    const placeholderOption = document.createElement('option');
    placeholderOption.value = '';
    placeholderOption.textContent = this.placeholderValue;
    this.targetSelect.appendChild(placeholderOption);

    // Add new options
    options.forEach((option) => {
      const optionElement = document.createElement('option');
      optionElement.value = option[this.valueFieldValue];
      optionElement.textContent = option[this.labelFieldValue];
      this.targetSelect.appendChild(optionElement);
    });

    // Restore the selected value if it exists in the new options
    if (currentValue) {
      const optionExists = Array.from(this.targetSelect.options).some(
        (option) => option.value === currentValue
      );
      if (optionExists) {
        this.targetSelect.value = currentValue;
      }
    }
  }

  clearOptions() {
    this.targetSelect.innerHTML = '';
    const placeholderOption = document.createElement('option');
    placeholderOption.value = '';
    placeholderOption.textContent = this.placeholderValue;
    this.targetSelect.appendChild(placeholderOption);
  }

  setLoading(loading) {
    this.targetSelect.disabled = loading;
    if (loading) {
      this.targetSelect.setAttribute('data-loading', 'true');
    } else {
      this.targetSelect.removeAttribute('data-loading');
    }
  }
}
